import { useEffect, useMemo, useRef, useState } from 'react';

import { Card } from '@components/common/Card';
import { Container } from '@components/common/Container';

const SUBTITLE =
  'Founded in Fall 2017 • Meeting at Bethlehem Baptist Church, Knightdale, NC';

const COUNT_UP_DURATION_MS = 1600;

/** Ease out cubic for smooth deceleration at the end */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const STATS = [
  {
    label: 'Classes per grade',
    valueForCount: 0,
    description: 'Average for Kindergarten through 12th',
  },
  {
    label: 'Number of Tutors',
    valueForCount: 15,
    prefix: '12-',
    description: 'Board-approved tutors',
  },
  {
    label: 'Classes Offered',
    valueForCount: 35,
    prefix: '25-',
    description: 'Preschool through Adult',
  },
] as const;

export interface AtAGlanceProps {
  /** Average approved classes covering each grade K–12 (excludes preschool and adult). */
  avgK12ClassesPerGrade?: number;
  /** When provided, "Number of Tutors" uses this count (unique teachers on approved scheduled classes). */
  tutorsCount?: number;
  /** When provided, "Classes Offered" uses this count (from active semester approved classes). */
  classesCount?: number;
}

function formatStatDisplay(
  stat: (typeof STATS)[number],
  count: number,
  useLiveValue?: boolean
): string {
  if (useLiveValue) return String(count);
  if ('prefix' in stat && stat.prefix != null) return `${stat.prefix}${count}`;
  return String(count);
}

export function AtAGlance({
  avgK12ClassesPerGrade,
  tutorsCount,
  classesCount,
}: AtAGlanceProps = {}) {
  const sectionRef = useRef<HTMLElement>(null);
  const hasAnimatedRef = useRef(false);
  /* Use 0 when API hasn't returned yet so we never animate to fake STATS values. */
  const avgTarget = avgK12ClassesPerGrade ?? 0;
  const tutorsTarget = tutorsCount ?? 0;
  const classesTarget = classesCount ?? 0;
  const targetValues = useMemo(
    () => [avgTarget, tutorsTarget, classesTarget],
    [avgTarget, tutorsTarget, classesTarget]
  );
  const [counts, setCounts] = useState<number[]>(() => [0, 0, 0]);
  const [hasBeenInView, setHasBeenInView] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);

  /** When live counts arrive after initial animation, update displayed values (adjust during render). */
  const [prevTargetValues, setPrevTargetValues] = useState(targetValues);
  if (prevTargetValues !== targetValues) {
    setPrevTargetValues(targetValues);
    const hasAnyLiveValue =
      avgK12ClassesPerGrade != null || tutorsCount != null || classesCount != null;
    if (animationComplete && hasAnyLiveValue) {
      setCounts([...targetValues]);
    }
  }

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;
          setHasBeenInView(true);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasBeenInView) return;

    const startTime = performance.now();
    let rafId: number;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / COUNT_UP_DURATION_MS, 1);
      const eased = easeOutCubic(t);
      setCounts(
        targetValues.map((target) => Math.round(target * eased))
      );
      if (t >= 1) setAnimationComplete(true);
      if (t < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [hasBeenInView, targetValues]);

  return (
    <section ref={sectionRef} id="at-a-glance" className="bhg-landing-stats" aria-labelledby="at-a-glance-heading">
      <div className="bhg-landing-stats__background">
        <div className="bhg-landing-stats__overlay" />
      </div>
      <Container size="lg">
        <div className="bhg-landing-stats__header">
          <h2 id="at-a-glance-heading" className="bhg-landing-stats__title">At a Glance</h2>
          <p className="bhg-landing-stats__subtitle">{SUBTITLE}</p>
        </div>

        <div className="bhg-landing-stats__grid">
          {STATS.map((stat, index) => {
            const target = targetValues[index];
            const hasLiveData =
              (index === 0 && avgK12ClassesPerGrade != null) ||
              (index === 1 && tutorsCount != null) ||
              (index === 2 && classesCount != null);
            const showPlaceholder =
              (index === 0 && avgK12ClassesPerGrade == null) ||
              (index === 1 && tutorsCount == null) ||
              (index === 2 && classesCount == null);
            const displayValue = showPlaceholder
              ? '—'
              : formatStatDisplay(stat, counts[index], hasLiveData);
            const ariaLabel = showPlaceholder ? `${stat.label}: loading` : formatStatDisplay(stat, target, hasLiveData);
            return (
              <Card
                key={index}
                elevation={2}
                className="bhg-landing-stat-card"
              >
                <div
                  className={`bhg-landing-stat-card__value${showPlaceholder ? ' bhg-landing-stat-card__value--placeholder' : ''}`}
                  aria-label={ariaLabel}
                >
                  {displayValue}
                </div>
                <h3 className="bhg-landing-stat-card__label">{stat.label}</h3>
                <p className="bhg-landing-stat-card__description">
                  {stat.description}
                </p>
              </Card>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
